import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from "react";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Mail,
  Pencil,
  Plus,
  Printer,
  Save,
  Send,
  Trash2,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Dialog } from "@/components/ui/Dialog";
import { PageHeader } from "@/components/layout/PageHeader";
import { formatMoney, todayIso } from "@/lib/utils";
import { api } from "@/lib/api";

interface DayEntry {
  date: string;
  hours: number;
}

interface TaskEntry {
  id: string;
  taskName: string;
  days: DayEntry[];
  rate: number;
}

interface ProjectEntry {
  id: string;
  projectName: string;
  tasks: TaskEntry[];
}

interface InvoiceData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  fromContactName: string;
  fromName: string;
  fromAddress: string;
  fromCityStateZip: string;
  fromPhone: string;
  fromEmail: string;
  toName: string;
  toAddress: string;
  toCityStateZip: string;
  toPhone: string;
  toEmail: string;
  forDescription: string;
  projects: ProjectEntry[];
  taxRate: number;
  notes: string;
}

// ─── Saved Clients & Business Profile ────────────────────────────────

interface SavedClient {
  id: string;
  name: string;
  address: string;
  cityStateZip: string;
  phone: string;
  email: string;
}

interface BusinessProfile {
  contactName: string;
  name: string;
  address: string;
  cityStateZip: string;
  phone: string;
  email: string;
}

const CLIENTS_KEY = "firesoft_invoice_clients";
const PROFILE_KEY = "firesoft_invoice_profile";
const INVOICE_KEY = "firesoft_invoice_draft_v3";

interface SavedInvoiceRecord {
  id: string;
  invoiceNumber: string;
  fileName: string;
  issueDate: string;
  toName: string;
  total: number;
  savedAt: string;
  data: InvoiceData;
  sentAt?: string;
  sentTo?: string;
  sentCc?: string;
}

interface ApiInvoice {
  id: string;
  number: string;
  issueDate: string;
  dueDate: string;
  status: number;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  jsonData?: string;
  sentOn?: string;
  sentTo?: string;
  sentCc?: string;
  toName?: string;
  periodStart?: string;
  periodEnd?: string;
  createdAt: string;
}

function apiInvoiceToRecord(inv: ApiInvoice): SavedInvoiceRecord | null {
  const data = inv.jsonData ? JSON.parse(inv.jsonData) as InvoiceData : null;
  return {
    id: inv.id,
    invoiceNumber: inv.number,
    fileName: `firesoft-inv-${inv.number}`,
    issueDate: inv.issueDate,
    toName: inv.toName || data?.toName || "",
    total: inv.total,
    savedAt: inv.createdAt,
    data: data ?? buildFallbackData(inv),
    sentAt: inv.sentOn ?? undefined,
    sentTo: inv.sentTo ?? undefined,
    sentCc: inv.sentCc ?? undefined,
  };
}

function buildFallbackData(inv: ApiInvoice): InvoiceData {
  const week = getWeekOffset(-1);
  return {
    ...defaultInvoice,
    invoiceNumber: inv.number,
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    periodStart: inv.periodStart ?? week.start,
    periodEnd: inv.periodEnd ?? week.end,
    toName: inv.toName ?? "",
    notes: inv.notes ?? "",
    taxRate: inv.subtotal > 0 ? (inv.tax / inv.subtotal) * 100 : 0,
  };
}

async function loadSavedInvoicesFromApi(retries = 3, delayMs = 2000): Promise<SavedInvoiceRecord[]> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await api().get<ApiInvoice[]>("/api/invoices");
      return res.data
        .map(apiInvoiceToRecord)
        .filter((r): r is SavedInvoiceRecord => r !== null);
    } catch {
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
      }
    }
  }
  return [];
}

async function saveInvoiceToApi(record: SavedInvoiceRecord): Promise<void> {
  const data = record.data;
  await api().put("/api/invoices/save", {
    invoiceNumber: data.invoiceNumber,
    issueDate: data.issueDate,
    dueDate: data.dueDate,
    periodStart: data.periodStart || null,
    periodEnd: data.periodEnd || null,
    toName: data.toName || null,
    subtotal: data.projects.reduce((s, p) => s + getProjectTotal(p), 0),
    tax: data.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (data.taxRate / 100),
    total: data.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (1 + data.taxRate / 100),
    notes: data.notes || null,
    jsonData: JSON.stringify(data),
    sentAt: record.sentAt || null,
    sentTo: record.sentTo || null,
    sentCc: record.sentCc || null,
  });
}

async function deleteInvoiceFromApi(id: string): Promise<void> {
  await api().delete(`/api/invoices/${id}`);
}

const INV_PREFIX_KEY = "firesoft_invoice_prefix";

function loadInvoicePrefix(): string {
  return localStorage.getItem(INV_PREFIX_KEY) ?? "INV-";
}

function saveInvoicePrefix(prefix: string) {
  localStorage.setItem(INV_PREFIX_KEY, prefix);
}

function getNextInvoiceNumber(saved: SavedInvoiceRecord[]): string {
  const year = new Date().getFullYear();
  const prefix = loadInvoicePrefix();
  let maxSeq = 0;
  for (const s of saved) {
    const match = s.invoiceNumber.match(/(\d{4})-(\d+)$/);
    if (match && match[1] === String(year)) {
      maxSeq = Math.max(maxSeq, parseInt(match[2], 10));
    }
  }
  return `${prefix}${year}-${String(maxSeq + 1).padStart(3, "0")}`;
}

function buildFileName(invoice: InvoiceData): string {
  const [y, m, d] = invoice.issueDate.split("-");
  return `firesoft-inv-${invoice.invoiceNumber}-${m}.${d}.${y}`;
}

function loadClients(): SavedClient[] {
  try {
    const raw = localStorage.getItem(CLIENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveClients(clients: SavedClient[]) {
  localStorage.setItem(CLIENTS_KEY, JSON.stringify(clients));
}

function loadProfile(): BusinessProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveProfile(profile: BusinessProfile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

function loadInvoiceDraft(): InvoiceData | null {
  try {
    const raw = localStorage.getItem(INVOICE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as InvoiceData;
    const expectedDays = getWeekDays(draft.periodStart, draft.periodEnd);
    if (expectedDays.length !== 7) {
      const [sy, sm, sd] = draft.periodStart.split("-").map(Number);
      const startDate = new Date(sy, sm - 1, sd);
      const sunday = getSundayOfWeek(startDate);
      const saturday = new Date(sunday);
      saturday.setDate(saturday.getDate() + 6);
      draft.periodStart = isoDate(sunday);
      draft.periodEnd = isoDate(saturday);
    }
    const correctedDays = getWeekDays(draft.periodStart, draft.periodEnd);
    for (const project of draft.projects) {
      for (const task of project.tasks) {
        if (task.days.length !== correctedDays.length) {
          task.days = correctedDays.map((date) => {
            const existing = task.days.find((d) => d.date === date);
            return existing ?? { date, hours: 0 };
          });
        }
      }
    }
    return draft;
  } catch {
    return null;
  }
}

function saveInvoiceDraft(invoice: InvoiceData) {
  localStorage.setItem(INVOICE_KEY, JSON.stringify(invoice));
}

const defaultClients: SavedClient[] = [];

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getSundayOfWeek(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setDate(copy.getDate() - copy.getDay());
  return copy;
}

function getWeekOffset(offset: number): { start: string; end: string } {
  const today = new Date();
  const thisSunday = getSundayOfWeek(today);
  const targetSunday = new Date(thisSunday);
  targetSunday.setDate(targetSunday.getDate() + offset * 7);
  const targetSaturday = new Date(targetSunday);
  targetSaturday.setDate(targetSaturday.getDate() + 6);
  return { start: isoDate(targetSunday), end: isoDate(targetSaturday) };
}

function getWeekDays(startIso: string, endIso: string): string[] {
  const days: string[] = [];
  const [sy, sm, sd] = startIso.split("-").map(Number);
  const [ey, em, ed] = endIso.split("-").map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(isoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function fmtShortDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtDayName(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { weekday: "short" });
}

function fmtWeekLabel(start: string, end: string): string {
  if (!start || !end) return "";
  const [y] = end.split("-").map(Number);
  return `${fmtShortDate(start)} – ${fmtShortDate(end)}, ${y}`;
}

function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return isoDate(d);
}

const prevWeek = getWeekOffset(-1);
const prevWeekDays = getWeekDays(prevWeek.start, prevWeek.end);

const defaultInvoice: InvoiceData = {
  invoiceNumber: getNextInvoiceNumber([]),
  issueDate: todayIso(),
  dueDate: defaultDueDate(),
  periodStart: prevWeek.start,
  periodEnd: prevWeek.end,
  fromContactName: "",
  fromName: "",
  fromAddress: "",
  fromCityStateZip: "",
  fromPhone: "",
  fromEmail: "",
  toName: "",
  toAddress: "",
  toCityStateZip: "",
  toPhone: "",
  toEmail: "",
  forDescription: "Consultation & Development Services",
  projects: [
    {
      id: crypto.randomUUID(),
      projectName: "",
      tasks: [
        {
          id: crypto.randomUUID(),
          taskName: "General Admin",
          days: prevWeekDays.map((date, i) => ({
            date,
            hours: [0, 0, 3.5, 5, 3, 2, 0][i] ?? 0,
          })),
          rate: 100,
        },
      ],
    },
  ],
  taxRate: 0,
  notes: "",
};

function getTaskHours(task: TaskEntry): number {
  return task.days.reduce((s, d) => s + d.hours, 0);
}

function getProjectHours(project: ProjectEntry): number {
  return project.tasks.reduce((s, t) => s + getTaskHours(t), 0);
}

function getProjectTotal(project: ProjectEntry): number {
  return project.tasks.reduce((s, t) => s + getTaskHours(t) * t.rate, 0);
}

export function InvoicesPage() {
  const [invoice, setInvoice] = useState<InvoiceData>(() => {
    const draft = loadInvoiceDraft();
    if (draft) return draft;
    const profile = loadProfile();
    const base = { ...defaultInvoice };
    if (profile) {
      base.fromContactName = profile.contactName;
      base.fromName = profile.name;
      base.fromAddress = profile.address;
      base.fromCityStateZip = profile.cityStateZip;
      base.fromPhone = profile.phone;
      base.fromEmail = profile.email;
    }
    return base;
  });
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  // Auto-save invoice on every change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    saveInvoiceDraft(invoice);
  }, [invoice]);
  const [clients, setClients] = useState<SavedClient[]>(() => {
    const stored = loadClients();
    if (stored.length === 0) {
      saveClients(defaultClients);
      return defaultClients;
    }
    const updated = stored.map((c) => {
      const def = defaultClients.find((d) => d.id === c.id);
      return def ? { ...c, email: def.email } : c;
    });
    if (JSON.stringify(updated) !== JSON.stringify(stored)) saveClients(updated);
    return updated;
  });
  const [profileSaved, setProfileSaved] = useState(false);
  const [invoiceSaved, setInvoiceSaved] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState<SavedInvoiceRecord[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoicesError(false);
    const records = await loadSavedInvoicesFromApi();
    setSavedInvoices(records);
    setInvoicesLoading(false);
    if (records.length === 0) {
      setInvoicesError(true);
    }
    return records;
  }, []);

  useEffect(() => {
    fetchInvoices().then((records) => {
      if (records.length > 0) {
        const mostRecent = records.reduce((a, b) =>
          (a.data.periodEnd ?? "") >= (b.data.periodEnd ?? "") ? a : b,
        );
        setInvoice(mostRecent.data);
        saveInvoiceDraft(mostRecent.data);
        setMode("preview");
      } else {
        const nextNum = getNextInvoiceNumber(records);
        setInvoice((prev) => ({ ...prev, invoiceNumber: nextNum }));
      }
    });
  }, []);

  const update = useCallback(
    <K extends keyof InvoiceData>(key: K, value: InvoiceData[K]) =>
      setInvoice((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const selectClient = useCallback((clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    setInvoice((prev) => ({
      ...prev,
      toName: client.name,
      toAddress: client.address,
      toCityStateZip: client.cityStateZip,
      toPhone: client.phone,
      toEmail: client.email,
    }));
  }, [clients]);

  const saveCurrentClient = useCallback(() => {
    const existing = clients.find((c) => c.name === invoice.toName);
    const clientData: SavedClient = {
      id: existing?.id ?? crypto.randomUUID(),
      name: invoice.toName,
      address: invoice.toAddress,
      cityStateZip: invoice.toCityStateZip,
      phone: invoice.toPhone,
      email: invoice.toEmail,
    };
    const updated = existing
      ? clients.map((c) => (c.id === existing.id ? clientData : c))
      : [...clients, clientData];
    setClients(updated);
    saveClients(updated);
  }, [invoice, clients]);

  const deleteClient = useCallback((clientId: string) => {
    const updated = clients.filter((c) => c.id !== clientId);
    setClients(updated);
    saveClients(updated);
  }, [clients]);

  const saveBusinessProfile = useCallback(() => {
    const profile: BusinessProfile = {
      contactName: invoice.fromContactName,
      name: invoice.fromName,
      address: invoice.fromAddress,
      cityStateZip: invoice.fromCityStateZip,
      phone: invoice.fromPhone,
      email: invoice.fromEmail,
    };
    saveProfile(profile);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2000);
  }, [invoice]);

  const handleSaveInvoice = useCallback(async () => {
    saveInvoiceDraft(invoice);
    const fileName = buildFileName(invoice);
    const record: SavedInvoiceRecord = {
      id: crypto.randomUUID(),
      invoiceNumber: invoice.invoiceNumber,
      fileName,
      issueDate: invoice.issueDate,
      toName: invoice.toName,
      total: invoice.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (1 + invoice.taxRate / 100),
      savedAt: new Date().toISOString(),
      data: { ...invoice },
    };
    try {
      await saveInvoiceToApi(record);
      const refreshed = await loadSavedInvoicesFromApi();
      setSavedInvoices(refreshed);
    } catch {
      const updated = [record, ...savedInvoices.filter((s) => s.invoiceNumber !== invoice.invoiceNumber)];
      setSavedInvoices(updated);
    }
    setInvoiceSaved(true);
    setTimeout(() => setInvoiceSaved(false), 2000);
  }, [invoice, savedInvoices]);

  const weekDays = useMemo(
    () => getWeekDays(invoice.periodStart, invoice.periodEnd),
    [invoice.periodStart, invoice.periodEnd],
  );

  const navigateToPeriod = useCallback((start: string, end: string) => {
    const match = savedInvoices.find(
      (r) => r.data.periodStart === start && r.data.periodEnd === end,
    );
    if (match) {
      setInvoice(match.data);
      saveInvoiceDraft(match.data);
      setMode("preview");
    } else {
      const days = getWeekDays(start, end);
      const nextNum = getNextInvoiceNumber(savedInvoices);
      setInvoice((prev) => ({
        ...prev,
        invoiceNumber: nextNum,
        issueDate: todayIso(),
        dueDate: defaultDueDate(),
        periodStart: start,
        periodEnd: end,
        projects: prev.projects.map((p) => ({
          ...p,
          tasks: p.tasks.map((t) => ({
            ...t,
            days: days.map((date) => {
              const existing = t.days.find((d) => d.date === date);
              return existing ?? { date, hours: 0 };
            }),
          })),
        })),
      }));
      setMode("edit");
    }
  }, [savedInvoices]);

  const setWeek = useCallback((offset: number) => {
    const wk = getWeekOffset(offset);
    navigateToPeriod(wk.start, wk.end);
  }, [navigateToPeriod]);

  const shiftWeek = useCallback((direction: -1 | 1) => {
    const s = new Date(invoice.periodStart + "T00:00:00");
    s.setDate(s.getDate() + direction * 7);
    const e = new Date(invoice.periodEnd + "T00:00:00");
    e.setDate(e.getDate() + direction * 7);
    navigateToPeriod(isoDate(s), isoDate(e));
  }, [invoice.periodStart, invoice.periodEnd, navigateToPeriod]);

  const updateDayHours = useCallback(
    (projectId: string, taskId: string, date: string, hours: number) => {
      setInvoice((prev) => ({
        ...prev,
        projects: prev.projects.map((p) =>
          p.id !== projectId
            ? p
            : {
                ...p,
                tasks: p.tasks.map((t) =>
                  t.id !== taskId
                    ? t
                    : {
                        ...t,
                        days: t.days.map((d) => (d.date === date ? { ...d, hours } : d)),
                      },
                ),
              },
        ),
      }));
    },
    [],
  );

  const updateTaskRate = useCallback((projectId: string, taskId: string, rate: number) => {
    setInvoice((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id !== projectId
          ? p
          : { ...p, tasks: p.tasks.map((t) => (t.id !== taskId ? t : { ...t, rate })) },
      ),
    }));
  }, []);

  const updateTaskName = useCallback((projectId: string, taskId: string, taskName: string) => {
    setInvoice((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id !== projectId
          ? p
          : { ...p, tasks: p.tasks.map((t) => (t.id !== taskId ? t : { ...t, taskName })) },
      ),
    }));
  }, []);

  const updateProjectName = useCallback((projectId: string, projectName: string) => {
    setInvoice((prev) => ({
      ...prev,
      projects: prev.projects.map((p) => (p.id !== projectId ? p : { ...p, projectName })),
    }));
  }, []);

  const addTask = useCallback(
    (projectId: string) => {
      setInvoice((prev) => ({
        ...prev,
        projects: prev.projects.map((p) =>
          p.id !== projectId
            ? p
            : {
                ...p,
                tasks: [
                  ...p.tasks,
                  {
                    id: crypto.randomUUID(),
                    taskName: "",
                    days: weekDays.map((date) => ({ date, hours: 0 })),
                    rate: p.tasks[0]?.rate ?? 100,
                  },
                ],
              },
        ),
      }));
    },
    [weekDays],
  );

  const removeTask = useCallback((projectId: string, taskId: string) => {
    setInvoice((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id !== projectId
          ? p
          : { ...p, tasks: p.tasks.filter((t) => t.id !== taskId) },
      ),
    }));
  }, []);

  const addProject = useCallback(() => {
    setInvoice((prev) => ({
      ...prev,
      projects: [
        ...prev.projects,
        {
          id: crypto.randomUUID(),
          projectName: "",
          tasks: [
            {
              id: crypto.randomUUID(),
              taskName: "",
              days: weekDays.map((date) => ({ date, hours: 0 })),
              rate: 100,
            },
          ],
        },
      ],
    }));
  }, [weekDays]);

  const removeProject = useCallback((projectId: string) => {
    setInvoice((prev) => ({
      ...prev,
      projects: prev.projects.filter((p) => p.id !== projectId),
    }));
  }, []);

  const totalHours = useMemo(
    () => invoice.projects.reduce((s, p) => s + getProjectHours(p), 0),
    [invoice.projects],
  );
  const subtotal = useMemo(
    () => invoice.projects.reduce((s, p) => s + getProjectTotal(p), 0),
    [invoice.projects],
  );
  const tax = subtotal * (invoice.taxRate / 100);
  const total = subtotal + tax;

  const handlePrint = useCallback(() => {
    const el = printRef.current;
    if (!el) return;

    const styleId = "invoice-print-style";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = `
      @media print {
        body * { visibility: hidden !important; }
        [data-invoice-print], [data-invoice-print] * { visibility: visible !important; }
        [data-invoice-print] {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          padding: 30px 40px !important;
          background: white !important;
        }
      }
    `;
    el.setAttribute("data-invoice-print", "true");
    const originalTitle = document.title;
    document.title = buildFileName(invoice);
    window.print();
    document.title = originalTitle;
    el.removeAttribute("data-invoice-print");
  }, [invoice]);

  const [emailOpen, setEmailOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const matchedInvoice = useMemo(
    () => savedInvoices.find((r) => r.data.periodStart === invoice.periodStart && r.data.periodEnd === invoice.periodEnd),
    [savedInvoices, invoice.periodStart, invoice.periodEnd],
  );

  const startNewForPeriod = useCallback(() => {
    const nextNum = getNextInvoiceNumber(savedInvoices);
    const profile = loadProfile();
    const days = getWeekDays(invoice.periodStart, invoice.periodEnd);
    const fresh: InvoiceData = {
      ...defaultInvoice,
      invoiceNumber: nextNum,
      periodStart: invoice.periodStart,
      periodEnd: invoice.periodEnd,
      projects: [{
        id: crypto.randomUUID(),
        projectName: "",
        tasks: [{
          id: crypto.randomUUID(),
          taskName: "General Admin",
          days: days.map((date) => ({ date, hours: 0 })),
          rate: 100,
        }],
      }],
    };
    if (profile) {
      fresh.fromContactName = profile.contactName;
      fresh.fromName = profile.name;
      fresh.fromAddress = profile.address;
      fresh.fromCityStateZip = profile.cityStateZip;
      fresh.fromPhone = profile.phone;
      fresh.fromEmail = profile.email;
    }
    setInvoice(fresh);
    saveInvoiceDraft(fresh);
    setMode("edit");
  }, [savedInvoices, invoice.periodStart, invoice.periodEnd]);

  return (
    <>
      <PageHeader
        eyebrow="Invoices"
        title="Invoice Generator"
        description="Create professional invoices for your consulting services. Fill in the details and print or save as PDF."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setHistoryOpen(true)}>
              <Clock className="h-4 w-4" /> History
              {savedInvoices.length > 0 && (
                <span className="ml-1 text-xs text-zinc-400">({savedInvoices.length})</span>
              )}
            </Button>
            <Button variant="outline" onClick={handleSaveInvoice}>
              {invoiceSaved ? (
                <><UserCheck className="h-4 w-4 text-emerald-400" /> Saved!</>
              ) : (
                <><Save className="h-4 w-4" /> Save</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setMode(mode === "edit" ? "preview" : "edit")}
            >
              {mode === "edit" ? (
                <><Eye className="h-4 w-4" /> Preview</>
              ) : (
                <><Pencil className="h-4 w-4" /> Edit</>
              )}
            </Button>
            <Button variant="outline" onClick={() => setEmailOpen(true)}>
              <Mail className="h-4 w-4" />
              Email Invoice
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="h-4 w-4" />
              Print / Save PDF
            </Button>
          </div>
        }
      />

      {/* API loading / retry banner */}
      {invoicesLoading && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-500/30 bg-blue-500/[0.06] px-4 py-2.5">
          <Clock className="h-4 w-4 text-blue-400 shrink-0 animate-spin" />
          <span className="text-sm text-zinc-300">Loading invoices from server (API may be warming up)...</span>
        </div>
      )}
      {!invoicesLoading && invoicesError && savedInvoices.length === 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-2.5">
          <Clock className="h-4 w-4 text-amber-400 shrink-0" />
          <span className="flex-1 text-sm text-zinc-300">Could not load invoices — the API may still be starting up.</span>
          <Button variant="outline" size="sm" onClick={fetchInvoices}>
            Retry
          </Button>
        </div>
      )}

      {/* Period status bar */}
      {matchedInvoice && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-2.5">
          <FileText className="h-4 w-4 text-emerald-400 shrink-0" />
          <div className="flex-1 min-w-0 flex items-center gap-3">
            <span className="text-sm text-zinc-200">
              <span className="font-mono font-semibold">{matchedInvoice.invoiceNumber}</span>
              {" "}· {matchedInvoice.toName || "—"} · <span className="tabular-nums">{formatMoney(matchedInvoice.total)}</span>
            </span>
            {matchedInvoice.sentAt ? (
              <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
                <Check className="h-2.5 w-2.5" /> Sent
              </span>
            ) : (
              <span className="text-[10px] font-medium text-zinc-500 bg-zinc-700/50 px-1.5 py-0.5 rounded">Draft</span>
            )}
          </div>
          <Button variant="outline" size="sm" className="shrink-0" onClick={startNewForPeriod}>
            <Plus className="h-3.5 w-3.5" /> New for this period
          </Button>
        </div>
      )}

      {/* Period navigation — always visible */}
      <Card className="mb-6 border-primary/30 bg-primary/[0.04]">
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
              Service Period
            </h3>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setWeek(-2)}>2 wks ago</Button>
              <Button variant="outline" size="sm" onClick={() => setWeek(-1)}>Last week</Button>
              <Button variant="outline" size="sm" onClick={() => setWeek(0)}>This week</Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[180px] text-center text-sm font-medium">
                {fmtWeekLabel(invoice.periodStart, invoice.periodEnd)}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Period Start">
              <Input type="date" value={invoice.periodStart} onChange={(e) => update("periodStart", e.target.value)} />
            </Field>
            <Field label="Period End">
              <Input type="date" value={invoice.periodEnd} onChange={(e) => update("periodEnd", e.target.value)} />
            </Field>
            <Field label="Total Hours">
              <div className="flex h-9 items-center rounded-md border border-border bg-muted/30 px-3 text-sm font-semibold tabular-nums">
                {totalHours}h
              </div>
            </Field>
            <Field label="Period Total">
              <div className="flex h-9 items-center rounded-md border border-border bg-muted/30 px-3 text-sm font-semibold tabular-nums">
                {formatMoney(subtotal)}
              </div>
            </Field>
          </div>
        </CardContent>
      </Card>

      {mode === "edit" ? (
        <InvoiceForm
          invoice={invoice}
          update={update}
          weekDays={weekDays}
          updateDayHours={updateDayHours}
          updateTaskRate={updateTaskRate}
          updateTaskName={updateTaskName}
          updateProjectName={updateProjectName}
          addTask={addTask}
          removeTask={removeTask}
          addProject={addProject}
          removeProject={removeProject}
          subtotal={subtotal}
          tax={tax}
          total={total}
          totalHours={totalHours}
          clients={clients}
          selectClient={selectClient}
          saveCurrentClient={saveCurrentClient}
          deleteClient={deleteClient}
          saveBusinessProfile={saveBusinessProfile}
          profileSaved={profileSaved}
          onPrefixChange={(num) => update("invoiceNumber", num)}
        />
      ) : null}

      <div className={mode === "edit" ? "mt-8" : ""}>
        <InvoicePreview
          ref={printRef}
          invoice={invoice}
          subtotal={subtotal}
          tax={tax}
          total={total}
          totalHours={totalHours}
          weekDays={weekDays}
        />
      </div>

      <EmailComposer
        key={emailOpen ? "open" : "closed"}
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSent={async (sentTo, sentCc) => {
          const fileName = buildFileName(invoice);
          const record: SavedInvoiceRecord = {
            id: crypto.randomUUID(),
            invoiceNumber: invoice.invoiceNumber,
            fileName,
            issueDate: invoice.issueDate,
            toName: invoice.toName,
            total: invoice.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (1 + invoice.taxRate / 100),
            savedAt: new Date().toISOString(),
            data: { ...invoice },
            sentAt: new Date().toISOString(),
            sentTo,
            sentCc: sentCc || undefined,
          };
          try {
            await saveInvoiceToApi(record);
            const refreshed = await loadSavedInvoicesFromApi();
            setSavedInvoices(refreshed);
          } catch {
            const updated = [record, ...savedInvoices.filter((s) => s.invoiceNumber !== invoice.invoiceNumber)];
            setSavedInvoices(updated);
          }
        }}
        invoice={invoice}
        totalHours={totalHours}
        total={total}
      />

      <InvoiceHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        invoices={savedInvoices}
        onLoad={(rec) => {
          setInvoice(rec.data);
          saveInvoiceDraft(rec.data);
          setHistoryOpen(false);
        }}
        onDelete={async (id) => {
          try {
            await deleteInvoiceFromApi(id);
            const refreshed = await loadSavedInvoicesFromApi();
            setSavedInvoices(refreshed);
          } catch {
            setSavedInvoices(savedInvoices.filter((s) => s.id !== id));
          }
        }}
      />
    </>
  );
}

function InvoiceNumberSettings({ onPrefixChange, currentNumber }: { onPrefixChange: (prefix: string) => void; currentNumber: string }) {
  const [open, setOpen] = useState(false);
  const [prefix, setPrefix] = useState(loadInvoicePrefix);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors text-left"
      >
        Format: <span className="font-mono">{prefix}YYYY-NNN</span> — click to change
      </button>
    );
  }

  return (
    <div className="flex items-end gap-2">
      <Field label="Number prefix" className="flex-1">
        <Input
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="e.g. INV-"
          className="font-mono"
        />
      </Field>
      <div className="flex items-center gap-1 text-xs text-zinc-500 pb-2">
        <span className="font-mono">{prefix}{new Date().getFullYear()}-NNN</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mb-0.5"
        onClick={() => {
          saveInvoicePrefix(prefix);
          const oldMatch = currentNumber.match(/(\d{4}-\d+)$/);
          if (oldMatch) {
            onPrefixChange(`${prefix}${oldMatch[1]}`);
          }
          setOpen(false);
        }}
      >
        <Check className="h-3.5 w-3.5" /> Apply
      </Button>
    </div>
  );
}

function ClientSelector({
  invoice,
  update,
  clients,
  selectClient,
  saveCurrentClient,
  deleteClient,
}: {
  invoice: InvoiceData;
  update: <K extends keyof InvoiceData>(key: K, value: InvoiceData[K]) => void;
  clients: SavedClient[];
  selectClient: (id: string) => void;
  saveCurrentClient: () => void;
  deleteClient: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const activeClient = clients.find((c) => c.name === invoice.toName);

  return (
    <Card>
      <CardContent className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Bill To
        </h3>

        <Select
          value={activeClient?.id ?? "__custom"}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__new") {
              update("toName", "");
              update("toAddress", "");
              update("toCityStateZip", "");
              update("toPhone", "");
              update("toEmail", "");
              setEditing(true);
            } else if (val === "__custom") {
              setEditing(true);
            } else {
              selectClient(val);
              setEditing(false);
            }
          }}
        >
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
          {!activeClient && invoice.toName && (
            <option value="__custom">{invoice.toName} (unsaved)</option>
          )}
          <option value="__new">+ Add new client</option>
        </Select>

        {editing ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Client Name" className="sm:col-span-2">
                <Input value={invoice.toName} onChange={(e) => update("toName", e.target.value)} placeholder="Client Company, Inc." />
              </Field>
              <Field label="Address">
                <Input value={invoice.toAddress} onChange={(e) => update("toAddress", e.target.value)} placeholder="456 Business Pkwy" />
              </Field>
              <Field label="City, State, ZIP">
                <Input value={invoice.toCityStateZip} onChange={(e) => update("toCityStateZip", e.target.value)} placeholder="City, ST 00000" />
              </Field>
              <Field label="Phone">
                <Input value={invoice.toPhone} onChange={(e) => update("toPhone", e.target.value)} placeholder="(555) 555-5555" />
              </Field>
              <Field label="Email">
                <Input value={invoice.toEmail} onChange={(e) => update("toEmail", e.target.value)} placeholder="email@client.com" />
              </Field>
            </div>
            <div className="flex gap-2">
              {invoice.toName && (
                <Button variant="outline" size="sm" onClick={() => { saveCurrentClient(); setEditing(false); }}>
                  <Save className="h-3.5 w-3.5" /> {activeClient ? "Update client" : "Save as client"}
                </Button>
              )}
              {(activeClient || invoice.toName) && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Done</Button>
              )}
            </div>
          </>
        ) : invoice.toName ? (
          <div className="rounded-lg border border-border/60 px-4 py-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{invoice.toName}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditing(true)}>
                  <Pencil className="h-3 w-3" /> Edit
                </Button>
                {activeClient && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-400 hover:text-red-300" onClick={() => { deleteClient(activeClient.id); }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            {invoice.toAddress && <p className="text-xs text-zinc-400">{invoice.toAddress}</p>}
            {invoice.toCityStateZip && <p className="text-xs text-zinc-400">{invoice.toCityStateZip}</p>}
            <div className="flex gap-4 pt-0.5">
              {invoice.toPhone && <span className="text-xs text-zinc-500">{invoice.toPhone}</span>}
              {invoice.toEmail && <span className="text-xs text-zinc-500 truncate">{invoice.toEmail}</span>}
            </div>
          </div>
        ) : null}

        <div className="pt-2 border-t border-border/40">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Service Type
          </h3>
          <Field label="For">
            <Input value={invoice.forDescription} onChange={(e) => update("forDescription", e.target.value)} />
          </Field>
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceForm({
  invoice,
  update,
  weekDays,
  updateDayHours,
  updateTaskRate,
  updateTaskName,
  updateProjectName,
  addTask,
  removeTask,
  addProject,
  removeProject,
  subtotal,
  tax,
  total,
  totalHours,
  clients,
  selectClient,
  saveCurrentClient,
  deleteClient,
  saveBusinessProfile,
  profileSaved,
  onPrefixChange,
}: {
  invoice: InvoiceData;
  update: <K extends keyof InvoiceData>(key: K, value: InvoiceData[K]) => void;
  weekDays: string[];
  updateDayHours: (projectId: string, taskId: string, date: string, hours: number) => void;
  updateTaskRate: (projectId: string, taskId: string, rate: number) => void;
  updateTaskName: (projectId: string, taskId: string, name: string) => void;
  updateProjectName: (projectId: string, name: string) => void;
  addTask: (projectId: string) => void;
  removeTask: (projectId: string, taskId: string) => void;
  addProject: () => void;
  removeProject: (projectId: string) => void;
  subtotal: number;
  tax: number;
  total: number;
  totalHours: number;
  clients: SavedClient[];
  selectClient: (id: string) => void;
  saveCurrentClient: () => void;
  deleteClient: (id: string) => void;
  saveBusinessProfile: () => void;
  profileSaved: boolean;
  onPrefixChange: (prefix: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Timesheet: Projects > Tasks > Days */}
      <Card className="lg:col-span-2">
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Timesheet
            </h3>
            <Button variant="outline" size="sm" onClick={addProject}>
              <Plus className="h-3.5 w-3.5" /> Add project
            </Button>
          </div>

          {invoice.projects.map((project) => (
            <div key={project.id} className="rounded-lg border border-border/60 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Field label="Project" className="flex-1">
                  <Input
                    value={project.projectName}
                    onChange={(e) => updateProjectName(project.id, e.target.value)}
                    placeholder="e.g. Client - Project Name"
                  />
                </Field>
                {invoice.projects.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mt-5 h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeProject(project.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {project.tasks.map((task) => (
                <div key={task.id} className="ml-4 rounded-md border border-border/40 bg-muted/20 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Field label="Task" className="flex-1">
                      <Input
                        value={task.taskName}
                        onChange={(e) => updateTaskName(project.id, task.id, e.target.value)}
                        placeholder="e.g. General Admin"
                      />
                    </Field>
                    <Field label="Rate ($/hr)" className="w-28">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={task.rate || ""}
                        onChange={(e) => updateTaskRate(project.id, task.id, Number(e.target.value) || 0)}
                      />
                    </Field>
                    {project.tasks.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mt-5 h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removeTask(project.id, task.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {/* Day-by-day hours grid */}
                  <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${weekDays.length}, 1fr) auto` }}>
                    {weekDays.map((date) => (
                      <div key={date} className="text-center">
                        <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                          {fmtDayName(date)}
                        </div>
                        <div className="text-[10px] text-muted-foreground/70">
                          {fmtShortDate(date)}
                        </div>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          max="24"
                          className="mt-1 text-center text-xs h-8"
                          value={task.days.find((d) => d.date === date)?.hours || ""}
                          onChange={(e) =>
                            updateDayHours(project.id, task.id, date, Number(e.target.value) || 0)
                          }
                        />
                      </div>
                    ))}
                    <div className="flex flex-col items-center justify-end">
                      <div className="text-[10px] font-semibold uppercase text-muted-foreground">Total</div>
                      <div className="mt-1 flex h-8 w-14 items-center justify-center rounded-md bg-muted/40 text-xs font-semibold tabular-nums">
                        {getTaskHours(task)}h
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end text-xs text-muted-foreground">
                    {getTaskHours(task)}h × ${task.rate}/hr = <span className="ml-1 font-semibold text-foreground">{formatMoney(getTaskHours(task) * task.rate)}</span>
                  </div>
                </div>
              ))}

              <Button variant="ghost" size="sm" className="ml-4" onClick={() => addTask(project.id)}>
                <Plus className="h-3.5 w-3.5" /> Add task
              </Button>

              <div className="flex justify-end border-t border-border/40 pt-2 text-sm">
                <span className="text-muted-foreground">Project total:</span>
                <span className="ml-2 font-semibold tabular-nums">{getProjectHours(project)}h</span>
                <span className="mx-2 text-muted-foreground">|</span>
                <span className="font-semibold tabular-nums">{formatMoney(getProjectTotal(project))}</span>
              </div>
            </div>
          ))}

          {/* Grand totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Total hours</span>
                <span className="font-medium tabular-nums text-foreground">{totalHours}h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium tabular-nums">{formatMoney(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Tax Rate (%)</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  className="w-20 text-right"
                  value={invoice.taxRate || ""}
                  onChange={(e) => update("taxRate", Number(e.target.value) || 0)}
                />
              </div>
              {tax > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="tabular-nums">{formatMoney(tax)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <span>Total</span>
                <span className="tabular-nums">{formatMoney(total)}</span>
              </div>
            </div>
          </div>

          <Field label="Notes / Payment Instructions">
            <Input
              value={invoice.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Payment terms, notes, etc."
            />
          </Field>
        </CardContent>
      </Card>

      {/* Invoice Details + From */}
      <Card>
        <CardContent className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Invoice Details
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <Field label="Invoice #" className="sm:col-span-2">
              <Input value={invoice.invoiceNumber} onChange={(e) => update("invoiceNumber", e.target.value)} />
            </Field>
            <Field label="Issue Date">
              <Input type="date" value={invoice.issueDate} onChange={(e) => update("issueDate", e.target.value)} />
            </Field>
            <Field label="Due Date">
              <Input type="date" value={invoice.dueDate} onChange={(e) => update("dueDate", e.target.value)} />
            </Field>
          </div>
          <InvoiceNumberSettings onPrefixChange={onPrefixChange} currentNumber={invoice.invoiceNumber} />
          <div className="flex items-center justify-between pt-2">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              From (Your Business)
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={saveBusinessProfile}
              className={profileSaved ? "border-emerald-500/50 text-emerald-400" : ""}
            >
              {profileSaved ? (
                <><UserCheck className="h-3.5 w-3.5" /> Saved!</>
              ) : (
                <><Save className="h-3.5 w-3.5" /> Save profile</>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Fill in your info once, hit &quot;Save profile&quot; — it&apos;ll auto-load next time.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Your Name" className="sm:col-span-2">
              <Input value={invoice.fromContactName} onChange={(e) => update("fromContactName", e.target.value)} placeholder="Zachary Smith" />
            </Field>
            <Field label="Business Name" className="sm:col-span-2">
              <Input value={invoice.fromName} onChange={(e) => update("fromName", e.target.value)} />
            </Field>
            <Field label="Address">
              <Input value={invoice.fromAddress} onChange={(e) => update("fromAddress", e.target.value)} placeholder="123 Main St" />
            </Field>
            <Field label="City, State, ZIP">
              <Input value={invoice.fromCityStateZip} onChange={(e) => update("fromCityStateZip", e.target.value)} placeholder="Milwaukee, WI 53202" />
            </Field>
            <Field label="Phone">
              <Input value={invoice.fromPhone} onChange={(e) => update("fromPhone", e.target.value)} placeholder="(555) 555-5555" />
            </Field>
            <Field label="Email">
              <Input type="email" value={invoice.fromEmail} onChange={(e) => update("fromEmail", e.target.value)} placeholder="billing@firesoft.io" />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Bill To */}
      <ClientSelector
        invoice={invoice}
        update={update}
        clients={clients}
        selectClient={selectClient}
        saveCurrentClient={saveCurrentClient}
        deleteClient={deleteClient}
      />
    </div>
  );
}

const InvoicePreview = forwardRef<
  HTMLDivElement,
  {
    invoice: InvoiceData;
    subtotal: number;
    tax: number;
    total: number;
    totalHours: number;
    weekDays: string[];
  }
>(function InvoicePreview({ invoice, subtotal, tax, total, totalHours, weekDays }, ref) {
  const fmtDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  const fmtDateShort = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const fmtMoney = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const metaLabelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#888",
    marginBottom: 4,
  };

  const thStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#888",
    padding: "6px 8px",
    borderBottom: "2px solid #e5e5e5",
  };

  return (
    <Card className="overflow-hidden">
      <div ref={ref} className="bg-white p-8 text-gray-900 sm:p-10">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>
              {invoice.fromName || "Your Business"}
            </div>
            {invoice.fromAddress && (
              <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                {invoice.fromAddress}
                {invoice.fromCityStateZip && <>, {invoice.fromCityStateZip}</>}
              </div>
            )}
            {(invoice.fromPhone || invoice.fromEmail) && (
              <div style={{ fontSize: 13, color: "#666" }}>
                {[invoice.fromPhone, invoice.fromEmail].filter(Boolean).join(" | ")}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: "#444" }}>
              {invoice.invoiceNumber}
            </div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              Issued: {fmtDate(invoice.issueDate)}
            </div>
          </div>
        </div>

        {/* Metadata row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 20, marginBottom: 32 }}>
          <div>
            <div style={metaLabelStyle}>Bill To</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              {invoice.toName && <div style={{ fontWeight: 600 }}>{invoice.toName}</div>}
              {invoice.toAddress && <div>{invoice.toAddress}</div>}
              {invoice.toCityStateZip && <div>{invoice.toCityStateZip}</div>}
              {invoice.toEmail && <div>{invoice.toEmail}</div>}
              {invoice.toPhone && <div>{invoice.toPhone}</div>}
              {!invoice.toName && <div style={{ color: "#bbb", fontStyle: "italic" }}>Client details</div>}
            </div>
          </div>
          <div>
            <div style={metaLabelStyle}>For</div>
            <div style={{ fontSize: 13 }}>{invoice.forDescription || "—"}</div>
          </div>
          <div>
            <div style={metaLabelStyle}>Service Period</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {fmtDateShort(invoice.periodStart)} – {fmtDateShort(invoice.periodEnd)}
            </div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
              {totalHours} hours
            </div>
          </div>
          <div>
            <div style={metaLabelStyle}>Due Date</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(invoice.dueDate)}</div>
          </div>
        </div>

        {/* Project > Task > Day breakdown */}
        {invoice.projects.map((project) => (
          <div key={project.id} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#444", padding: "8px 0", borderBottom: "1px solid #e5e5e5" }}>
              {project.projectName || "Project"}
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left", width: "30%" }}>Task</th>
                  {weekDays.map((date) => (
                    <th key={date} style={{ ...thStyle, textAlign: "center", width: `${50 / weekDays.length}%` }}>
                      {fmtDayName(date)}<br />
                      <span style={{ fontWeight: 400, fontSize: 9 }}>{fmtShortDate(date)}</span>
                    </th>
                  ))}
                  <th style={{ ...thStyle, textAlign: "right", width: "8%" }}>Hours</th>
                  <th style={{ ...thStyle, textAlign: "right", width: "10%" }}>Rate</th>
                  <th style={{ ...thStyle, textAlign: "right", width: "12%" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {project.tasks.map((task) => {
                  const hrs = getTaskHours(task);
                  return (
                    <tr key={task.id}>
                      <td style={{ padding: "8px 8px 8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12, fontWeight: 500 }}>
                        {task.taskName || "—"}
                      </td>
                      {weekDays.map((date) => {
                        const dayHrs = task.days.find((d) => d.date === date)?.hours || 0;
                        return (
                          <td key={date} style={{ padding: "8px 4px", borderBottom: "1px solid #f0f0f0", fontSize: 12, textAlign: "center", color: dayHrs > 0 ? "#1a1a1a" : "#ccc", fontVariantNumeric: "tabular-nums" }}>
                            {dayHrs > 0 ? `${dayHrs}h` : "–"}
                          </td>
                        );
                      })}
                      <td style={{ padding: "8px 8px 8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {hrs}h
                      </td>
                      <td style={{ padding: "8px 8px 8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {fmtMoney(task.rate)}
                      </td>
                      <td style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {fmtMoney(hrs * task.rate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#666" }}>
                    Project Total
                  </td>
                  {weekDays.map((date) => {
                    const dayTotal = project.tasks.reduce(
                      (s, t) => s + (t.days.find((d) => d.date === date)?.hours || 0),
                      0,
                    );
                    return (
                      <td key={date} style={{ padding: "6px 4px", fontSize: 11, textAlign: "center", fontWeight: 600, color: dayTotal > 0 ? "#444" : "#ccc", fontVariantNumeric: "tabular-nums" }}>
                        {dayTotal > 0 ? `${dayTotal}h` : "–"}
                      </td>
                    );
                  })}
                  <td style={{ padding: "6px 8px 6px 0", fontSize: 11, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {getProjectHours(project)}h
                  </td>
                  <td />
                  <td style={{ padding: "6px 0", fontSize: 11, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {fmtMoney(getProjectTotal(project))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ))}

        {/* Totals */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <div style={{ width: 280 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
              <span style={{ color: "#666" }}>Total hours</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{totalHours}h</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
              <span style={{ color: "#666" }}>Subtotal</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMoney(subtotal)}</span>
            </div>
            {invoice.taxRate > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
                <span style={{ color: "#666" }}>Tax ({invoice.taxRate}%)</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMoney(tax)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 6px", fontSize: 16, fontWeight: 700, borderTop: "2px solid #1a1a1a", marginTop: 4 }}>
              <span>Total</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMoney(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        {invoice.notes && (
          <div style={{ marginTop: 40, paddingTop: 16, borderTop: "1px solid #e5e5e5", fontSize: 12, color: "#888", textAlign: "center" }}>
            {invoice.notes}
          </div>
        )}
        <div style={{ marginTop: 24, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: "#bbb", textAlign: "center" }}>
          Thank you for your business!
        </div>
      </div>
    </Card>
  );
});

// ─── Invoice History Dialog ──────────────────────────────────────────

function InvoiceHistoryDialog({
  open,
  onClose,
  invoices,
  onLoad,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  invoices: SavedInvoiceRecord[];
  onLoad: (rec: SavedInvoiceRecord) => void;
  onDelete: (id: string) => void;
}) {
  const [previewId, setPreviewId] = useState<string | null>(null);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invoice History"
      description={`${invoices.length} saved invoice${invoices.length !== 1 ? "s" : ""}`}
      size="lg"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {invoices.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">No invoices saved yet.</div>
        )}
        {invoices.map((rec) => (
          <div key={rec.id}>
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:bg-muted/20 transition-colors">
              <div className="flex items-center gap-4 min-w-0">
                {rec.sentAt ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded shrink-0">
                    <Send className="h-3 w-3" /> Sent
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-zinc-400 bg-zinc-700/50 px-2 py-1 rounded shrink-0">
                    <FileText className="h-3 w-3" /> Draft
                  </span>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold">{rec.invoiceNumber}</span>
                    <span className="text-sm text-zinc-400 truncate">{rec.toName || "—"}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {fmtShortDate(rec.data.periodStart)} – {fmtShortDate(rec.data.periodEnd)}
                    {rec.sentAt && rec.sentTo && (
                      <span className="ml-2">sent to {rec.sentTo.split("@")[0]}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <span className="text-sm font-semibold tabular-nums">{formatMoney(rec.total)}</span>
                <Button variant="ghost" size="sm" className="h-7" onClick={() => setPreviewId(previewId === rec.id ? null : rec.id)}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7" onClick={() => onLoad(rec)}>
                  Load
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                  onClick={() => onDelete(rec.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {previewId === rec.id && (
              <div className="mt-2 mb-3">
                <InvoicePreview
                  invoice={rec.data}
                  subtotal={rec.data.projects.reduce((s, p) => s + getProjectTotal(p), 0)}
                  tax={rec.data.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (rec.data.taxRate / 100)}
                  total={rec.data.projects.reduce((s, p) => s + getProjectTotal(p), 0) * (1 + rec.data.taxRate / 100)}
                  totalHours={rec.data.projects.reduce((s, p) => s + getProjectHours(p), 0)}
                  weekDays={getWeekDays(rec.data.periodStart, rec.data.periodEnd)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </Dialog>
  );
}

// ─── Email Composer ──────────────────────────────────────────────────

function EmailComposer({
  open,
  onClose,
  onSent,
  invoice,
  totalHours: _totalHours,
  total: _total,
}: {
  open: boolean;
  onClose: () => void;
  onSent?: (to: string, cc: string) => void;
  invoice: InvoiceData;
  totalHours: number;
  total: number;
}) {
  const periodLabel = `${fmtShortDate(invoice.periodStart)} – ${fmtShortDate(invoice.periodEnd)}, ${invoice.periodEnd.split("-")[0]}`;

  const clientEmails = (invoice.toEmail || "")
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
  const defaultTo = clientEmails[0] || "";
  const defaultCc = clientEmails.slice(1).join(", ");

  const defaultSubject = `Invoice ${invoice.invoiceNumber} – ${invoice.fromName} – ${periodLabel}`;
  const signOffName = [invoice.fromContactName, invoice.fromName]
    .filter(Boolean)
    .join("\n");
  const defaultBody = [
    `Hi,`,
    ``,
    `Please find attached invoice ${invoice.invoiceNumber} for the service period ${periodLabel}.`,
    ``,
    `Please let me know if you have any questions.`,
    ``,
    `Thank you,`,
    signOffName,
    [invoice.fromPhone, invoice.fromEmail].filter(Boolean).join(" | "),
  ].join("\n");

  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prevOpen = useRef(open);
  if (open && !prevOpen.current) {
    setTo(defaultTo);
    setCc(defaultCc);
    setSubject(defaultSubject);
    setBody(defaultBody);
    setSending(false);
    setSent(false);
    setError(null);
  }
  prevOpen.current = open;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      await api().post("/api/invoices/send-email", {
        to,
        cc: cc || null,
        subject,
        body,
        invoiceNumber: invoice.invoiceNumber,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        periodStart: invoice.periodStart,
        periodEnd: invoice.periodEnd,
        fromContactName: invoice.fromContactName || null,
        fromName: invoice.fromName,
        fromAddress: invoice.fromAddress || null,
        fromCityStateZip: invoice.fromCityStateZip || null,
        fromPhone: invoice.fromPhone || null,
        fromEmail: invoice.fromEmail || null,
        toName: invoice.toName,
        toAddress: invoice.toAddress || null,
        toCityStateZip: invoice.toCityStateZip || null,
        toPhone: invoice.toPhone || null,
        toEmail: invoice.toEmail || null,
        forDescription: invoice.forDescription || null,
        notes: invoice.notes || null,
        taxRate: invoice.taxRate,
        projects: invoice.projects.map((p) => ({
          projectName: p.projectName,
          tasks: p.tasks.map((t) => ({
            taskName: t.taskName,
            rate: t.rate,
            days: t.days.map((d) => ({ date: d.date, hours: d.hours })),
          })),
        })),
      });
      setSent(true);
      onSent?.(to, cc);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to send email";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Send Invoice"
      description={`Sends from ${invoice.fromEmail || "your configured email"} with PDF attached.`}
      size="lg"
      footer={
        sent ? (
          <div className="flex items-center gap-3 w-full">
            <span className="flex-1 text-xs text-emerald-400">
              Invoice sent to {to}{cc ? ` (cc: ${cc})` : ""} with PDF attached.
            </span>
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button onClick={handleSend} disabled={!to || !subject || sending}>
              <Mail className="h-4 w-4" />
              {sending ? "Sending..." : "Send Invoice"}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          <Field label="To">
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="finance@client.com"
            />
          </Field>
          <Field label="CC">
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="manager@client.com"
            />
          </Field>
          <Field label="Subject">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Field>
          <Field label="Body">
            <textarea
              className="min-h-[200px] w-full rounded-md border border-border bg-background p-3 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
        </div>

        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          The invoice PDF is generated and attached automatically. Sends from <strong>{invoice.fromEmail || "your configured email"}</strong> via your domain.
        </div>
      </div>
    </Dialog>
  );
}
